import { ref } from 'vue'
import type { Product as SwProduct } from '@hubblecommerce/hubble/platforms/shopware/api-client'
import { OpenAPI } from '@hubblecommerce/hubble/platforms/shopware/api-client'
import { request as __request } from '@hubblecommerce/hubble/platforms/shopware/request'
import { hblMapProductVariant } from '@/utils/mapping/hblMapProductVariant'

export function useProductVariants() {
    const allVariants = ref<SwProduct[]>([])
    const orderableVariants = ref<SwProduct[]>([])
    const variants = ref<SwProduct[]>([])
    const loading = ref(false)
    const error = ref<string | null>(null)
    const allVariantsNotOrderable = ref(false)

    /**
     * Fetches product variants from the Shopware API and applies data mapping
     * @param productId - The ID of the product to fetch variants for
     */
    async function fetchVariants(productId: string) {
        if (!productId) {
            error.value = 'Product ID is required'
            return
        }

        loading.value = true
        error.value = null

        try {
            const response = await __request(OpenAPI, {
                method: 'POST',
                url: '/product/{productId}/variants',
                path: {
                    productId
                },
                body: {
                    associations: {
                        options: {}
                    }
                }
            })

            // Map all variants through the mapping function
            const mappedVariants = response?.map((variant: SwProduct) => hblMapProductVariant(variant)) || []

            allVariants.value = mappedVariants

            if (allVariants.value.length === 0) {
                variants.value = []
                orderableVariants.value = []
                allVariantsNotOrderable.value = false
                return
            }

            // Filter orderable variants
            orderableVariants.value = allVariants.value.filter(variant =>
                variant?.deliveryTime?.customFields?.available_for_purchase !== 'available_for_purchase_not_orderable'
            )

            // Check if all variants are not orderable
            allVariantsNotOrderable.value = orderableVariants.value.length === 0 && allVariants.value.length > 0

            // Set variants to either orderable or all variants
            if (allVariantsNotOrderable.value) {
                variants.value = allVariants.value
            } else {
                variants.value = orderableVariants.value
            }
        } catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to fetch variants'
            console.error('Error fetching variants:', e)
        } finally {
            loading.value = false
        }
    }

    /**
     * Resets all variant state
     */
    function resetVariants() {
        allVariants.value = []
        orderableVariants.value = []
        variants.value = []
        error.value = null
        allVariantsNotOrderable.value = false
    }

    return {
        allVariants,
        orderableVariants,
        variants,
        loading,
        error,
        allVariantsNotOrderable,
        fetchVariants,
        resetVariants
    }
}
